// Progress Charts Module

import { Chart } from 'chart.js/auto';
import { dataManager } from './data.js';
import { storage } from './storage.js';

class ChartsManager {
  constructor() {
    this.volumeChart = null;
    this.exerciseChart = null;
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;

    // Listen for data loaded and theme changes
    window.addEventListener('dataloaded', () => this.updateProgressCharts());
    window.addEventListener('datachanged', () => this.updateProgressCharts());
    window.addEventListener('themechanged', () => this.updateChartColors());

    // Setup exercise selector
    const exerciseSelect = document.getElementById('exerciseSelect');
    if (exerciseSelect) {
      exerciseSelect.addEventListener('change', () => this.updateExerciseChart());
    }

    this.initialized = true;
  }

  updateProgressCharts() {
    this.updateVolumeChart();
    this.populateExerciseSelect();
  }

  updateVolumeChart() {
    const canvas = document.getElementById('volumeChart');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const weeks = ['W1', 'W2', 'W3', 'W4', 'W5', 'W6'];
    const volumes = weeks.map(week => this.calculateWeekVolume(week));

    const theme = document.documentElement.getAttribute('data-theme');
    const textColor = theme === 'dark' ? '#FFFFFF' : '#1D1D1F';
    const gridColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';

    if (this.volumeChart) {
      this.volumeChart.destroy();
    }

    this.volumeChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: weeks,
        datasets: [{
          label: 'Volume (kg)',
          data: volumes,
          backgroundColor: 'rgba(0, 122, 255, 0.6)',
          borderColor: '#007AFF',
          borderWidth: 2,
          borderRadius: 8
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              color: textColor,
              callback: function(value) {
                return value.toLocaleString() + 'kg';
              }
            },
            grid: {
              color: gridColor
            }
          },
          x: {
            ticks: {
              color: textColor
            },
            grid: {
              display: false
            }
          }
        }
      }
    });
  }

  calculateWeekVolume(week) {
    const data = dataManager.getData();
    if (!data || !data.training || !data.training[week]) return 0;

    let volume = 0;

    for (const day in data.training[week]) {
      const exercises = data.training[week][day];
      exercises.forEach((ex, index) => {
        if (this.isExerciseCompleted(week, day.split(' ')[0], index)) {
          const weight = parseInt(ex.carico) || 0;
          const reps = parseInt(ex.serie.split('x')[1]) || 0;
          const sets = parseInt(ex.serie.split('x')[0]) || 0;
          volume += weight * reps * sets;
        }
      });
    }

    return volume;
  }

  isExerciseCompleted(week, day, exerciseIndex) {
    const key = `workout-completion-${week}-${day}-${exerciseIndex}`;
    // storage.getItem returns boolean true (from JSON.parse), not string 'true'
    return storage.getItem(key) === true;
  }

  populateExerciseSelect() {
    const data = dataManager.getData();
    if (!data || !data.training) return;

    const select = document.getElementById('exerciseSelect');
    if (!select) return;

    const exercises = new Set();

    for (const week in data.training) {
      for (const day in data.training[week]) {
        data.training[week][day].forEach(ex => {
          exercises.add(ex.nome);
        });
      }
    }

    select.innerHTML = '<option value="">Seleziona esercizio...</option>';
    Array.from(exercises).sort().forEach(name => {
      const option = document.createElement('option');
      option.value = name;
      option.textContent = name;
      select.appendChild(option);
    });
  }

  updateExerciseChart() {
    const data = dataManager.getData();
    const exerciseSelect = document.getElementById('exerciseSelect');
    const canvas = document.getElementById('exerciseChart');

    if (!exerciseSelect || !canvas) return;

    const exerciseName = exerciseSelect.value;
    if (!exerciseName || !data || !data.training) return;

    const ctx = canvas.getContext('2d');
    const weeks = ['W1', 'W2', 'W3', 'W4', 'W5', 'W6'];
    const loads = [];

    weeks.forEach(week => {
      let maxLoad = 0;
      if (data.training[week]) {
        for (const day in data.training[week]) {
          const exercise = data.training[week][day].find(ex => ex.nome === exerciseName);
          if (exercise) {
            const load = parseInt(exercise.carico) || 0;
            if (load > maxLoad) maxLoad = load;
          }
        }
      }
      loads.push(maxLoad);
    });

    const theme = document.documentElement.getAttribute('data-theme');
    const textColor = theme === 'dark' ? '#FFFFFF' : '#1D1D1F';
    const gridColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';

    if (this.exerciseChart) {
      this.exerciseChart.destroy();
    }

    this.exerciseChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: weeks,
        datasets: [{
          label: exerciseName,
          data: loads,
          borderColor: '#007AFF',
          backgroundColor: 'rgba(0, 122, 255, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 6,
          pointBackgroundColor: '#007AFF',
          pointBorderColor: '#FFFFFF',
          pointBorderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              color: textColor,
              callback: function(value) {
                return value + 'kg';
              }
            },
            grid: {
              color: gridColor
            }
          },
          x: {
            ticks: {
              color: textColor
            },
            grid: {
              display: false
            }
          }
        }
      }
    });
  }

  updateChartColors() {
    if (this.volumeChart) this.updateVolumeChart();
    if (this.exerciseChart && document.getElementById('exerciseSelect')?.value) {
      this.updateExerciseChart();
    }
  }
}

// Initialize and export
const chartsManager = new ChartsManager();
chartsManager.init();

export { chartsManager };
